import React, { useState } from 'react';
import ApiClient from '../../utils/ApiClient';

/**
 * The status a row actually SHOWS (same derivation as the badge): Amazon's order_status,
 * except a unit pulled/sold on our end whose order hasn't synced to Shipped yet is the
 * interim "Pulled · awaiting sync".
 */
export function derivedStatus(order, isShipped) {
    if (order.order_status === 'Shipped') return 'Shipped';
    if (isShipped || order.sold === 1) return 'Pulled · awaiting sync';
    return order.order_status;
}

/**
 * Actionability rank (owner, 2026-09-08): what needs a human floats up. Unshipped first
 * (ship it now), then Pending (watch it), then the interim pulled state, then the done
 * states. Unknown statuses sort with Pending rather than vanishing to the bottom.
 */
const STATUS_RANK = {
    'Unshipped': 0,
    'PartiallyShipped': 0,
    'Pending': 1,
    'Pulled · awaiting sync': 2,
    'Shipped': 3,
    'Canceled': 4,
};

function rankOf(order, isShippedFn) {
    const rank = STATUS_RANK[derivedStatus(order, isShippedFn ? isShippedFn(order.order_location_id) : false)];
    return rank === undefined ? 1 : rank;
}

/**
 * modes: 'actionable' (rank asc, oldest purchase first within rank — the default, so an
 * order that activates after days in Pending surfaces instead of staying buried at its
 * purchase-date position), 'actionable-desc' (reversed), 'date' (purchase date, newest
 * first — the page's historical order).
 */
export function sortOrders(orders, mode, isShippedFn) {
    const rows = [...(orders || [])];
    const time = (o) => {
        const t = new Date(o.purchase_date).getTime();
        return Number.isFinite(t) ? t : 0;
    };
    if (mode === 'date') {
        return rows.sort((a, b) => time(b) - time(a));
    }
    const dir = mode === 'actionable-desc' ? -1 : 1;
    return rows.sort((a, b) => {
        const byRank = rankOf(a, isShippedFn) - rankOf(b, isShippedFn);
        if (byRank !== 0) return dir * byRank;
        return dir * (time(a) - time(b));
    });
}

export default function SellerFulfilledOrdersTable({
                                                       orders,
                                                       processingOrders,
                                                       handleOpenModal,
                                                       isOrderShipped,
                                                       onInventoryAssigned,
                                                       onUnitReleased
                                                   }) {
    const [cantFindBusy, setCantFindBusy] = useState(new Set());
    // Sort mode cycles via the Status header: actionable (default) → reversed → date.
    const [sortMode, setSortMode] = useState('actionable');
    const [confirmOrder, setConfirmOrder] = useState(null); // order pending confirmation
    const [confirmResult, setConfirmResult] = useState(null); // {message, isError}
    const [assignBusy, setAssignBusy] = useState(new Set());
    const [assignModalOrder, setAssignModalOrder] = useState(null);   // order currently in modal
    const [assignOptions, setAssignOptions] = useState(null);          // array of options or null while loading
    const [assignSelected, setAssignSelected] = useState(null);        // selected identifier
    const [assignError, setAssignError] = useState(null);
    const [assignSubmitting, setAssignSubmitting] = useState(false);

    const openAssignModal = async (order) => {
        if (!order || !order.order_location_id) return;
        setAssignModalOrder(order);
        setAssignOptions(null);
        setAssignSelected(null);
        setAssignError(null);
        try {
            const resp = await ApiClient.get('/api/orders/inventory-options', {
                params: { amazonOrdersId: order.id }
            });
            const opts = Array.isArray(resp.data) ? resp.data : [];
            setAssignOptions(opts);
            const preferred = opts.find(o => o.isPreferred && o.qty > 0);
            if (preferred) setAssignSelected(preferred.identifier);
            else if (opts.length === 1) setAssignSelected(opts[0].identifier);
        } catch (e) {
            setAssignError('Failed to load inventory options: ' + e.message);
            setAssignOptions([]);
        }
    };

    const closeAssignModal = () => {
        if (assignSubmitting) return;
        setAssignModalOrder(null);
        setAssignOptions(null);
        setAssignSelected(null);
        setAssignError(null);
    };

    const submitAssign = async () => {
        const order = assignModalOrder;
        if (!order || !assignSelected) return;
        setAssignSubmitting(true);
        setAssignBusy(prev => new Set([...prev, order.order_location_id]));
        try {
            const resp = await ApiClient.post('/api/orders/assign-inventory', {
                amazonOrdersId: order.id,
                orderLocationId: order.order_location_id,
                identifier: assignSelected,
            });
            const d = resp.data || {};
            if (d.success) {
                setAssignError(null);
                if (onInventoryAssigned) {
                    // Update the row in place from what the backend bound — the assigned
                    // FNSKU/location persist without a full page reload (which also wiped
                    // the in-memory shipped state).
                    onInventoryAssigned(order.order_location_id, {
                        identifier: d.identifier || assignSelected,
                        location: d.location,
                        inventoryId: d.inventoryId,
                    });
                    setAssignModalOrder(null);
                    setAssignOptions(null);
                    setAssignSelected(null);
                } else {
                    setTimeout(() => window.location.reload(), 400);
                }
            } else {
                setAssignError('Assign failed: ' + (d.message || 'unknown error'));
            }
        } catch (e) {
            setAssignError('Request failed: ' + e.message);
        } finally {
            setAssignSubmitting(false);
            setAssignBusy(prev => {
                const n = new Set(prev); n.delete(order.order_location_id); return n;
            });
        }
    };

    const openCantFindConfirm = (order) => {
        if (!order || !order.order_location_id) return;
        if (cantFindBusy.has(order.order_location_id)) return;
        setConfirmResult(null);
        setConfirmOrder(order);
    };

    const closeCantFindConfirm = () => {
        const reload = confirmResult && confirmResult.reloadOnClose;
        setConfirmOrder(null);
        setConfirmResult(null);
        if (reload) window.location.reload();   // row still shows the unit that was replaced
    };

    const submitCantFind = async () => {
        const order = confirmOrder;
        if (!order) return;
        setCantFindBusy(prev => new Set([...prev, order.order_location_id]));
        try {
            const resp = await ApiClient.post('/api/orders/cant-find-unit', {
                amazonOrdersId: order.id,
                orderLocationId: order.order_location_id,
                sellerSku: order.seller_sku,
                asin: order.asin,
            });
            const d = resp.data || {};
            if (d.success) {
                // A failed/skipped Amazon qty push leaves the listing advertising a unit we no
                // longer have (MERQ-YPB-C1NO, 2026-10-19). Keep it on screen until closed: no
                // auto-reload, no jump into the assign picker over it.
                if (d.amazonQtyUpdated === false) {
                    if (!d.reassigned && onUnitReleased) onUnitReleased(order.order_location_id);
                    const unitPart = d.reassigned
                        ? `Reassigned to inventory id ${d.newInventoryId}. `
                        : 'No replacement unit — assign one from the row after closing. ';
                    setConfirmResult({
                        message: `${unitPart}Amazon quantity was NOT updated for ${order.seller_sku || order.sku}: `
                            + `${d.amazonQtyError || 'unknown error'}. Set this listing's quantity in Seller Central.`,
                        isError: true,
                        reloadOnClose: !!d.reassigned,
                    });
                    return;
                }
                if (!d.reassigned) {
                    // No same-condition replacement available. Release the row in place,
                    // then chain into the assign picker so the user can pick a different
                    // condition/location unit to fulfill the order.
                    if (onUnitReleased) onUnitReleased(order.order_location_id);
                    closeCantFindConfirm();
                    openAssignModal(order);
                    return;
                }
                const amzPart = d.amazonQtyUpdated
                    ? ` Amazon qty set to ${d.amazonQty}.`
                    : (d.amazonQtyError ? ` Amazon qty update failed: ${d.amazonQtyError}.` : '');
                setConfirmResult({ message: `Reassigned to inventory id ${d.newInventoryId}.${amzPart}`, isError: false });
                setTimeout(() => window.location.reload(), 1500);
            } else {
                setConfirmResult({ message: 'Action failed: ' + (d.message || 'unknown error'), isError: true });
            }
        } catch (e) {
            setConfirmResult({ message: 'Request failed: ' + e.message, isError: true });
        } finally {
            setCantFindBusy(prev => {
                const n = new Set(prev); n.delete(order.order_location_id); return n;
            });
        }
    };
    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="mt-8">
                <div className="overflow-x-auto">
                    <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden shadow-sm ring-1 ring-black ring-opacity-5">
                            <table className="min-w-full divide-y divide-gray-300 table-fixed">
                                <colgroup>
                                    <col className="w-16" />
                                    <col className="w-1/4" />
                                    <col className="w-48" />
                                    <col className="w-32" />
                                    <col className="w-24" />
                                    <col className="w-32" />
                                    <col className="w-48" />
                                    <col className="w-20" />
                                </colgroup>
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Photo</th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Product</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Identifiers</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        <button
                                            type="button"
                                            aria-label="Sort by status"
                                            onClick={() => setSortMode(m =>
                                                m === 'actionable' ? 'actionable-desc'
                                                    : m === 'actionable-desc' ? 'date' : 'actionable')}
                                            title={sortMode === 'date'
                                                ? 'Sorted by purchase date (newest first) — click to sort by status'
                                                : 'Sorted by status: what needs action first — click to change'}
                                            className="inline-flex items-center gap-1 font-semibold text-gray-900 hover:text-indigo-700"
                                        >
                                            Amz Status
                                            <span aria-hidden="true" className="text-xs">
                                                {sortMode === 'actionable' ? '▲' : sortMode === 'actionable-desc' ? '▼' : '·'}
                                            </span>
                                        </button>
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Ship</th>
                                    <th scope="col" className="relative py-3.5 pl-3 pr-4">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                {sortOrders(orders, sortMode, isOrderShipped).map((order, index) => {
                                    const rowKey = order.order_location_id;
                                    // Per-row shipped state: keyed on order_location_id so multi-qty
                                    // orders (one amazon_orders row, N link rows) track independently.
                                    const isShipped = isOrderShipped ? isOrderShipped(rowKey) : order.sold === 1;
                                    // Amazon-confirmed shipped (synced from Orders API) vs. pulled on our
                                    // end (unit sold=1 / shipped this session) but order_status not yet
                                    // synced to 'Shipped'. The latter is an interim "awaiting sync" state.
                                    const isAmazonShipped = order.order_status === 'Shipped';
                                    const isPulledAwaitingSync = !isAmazonShipped && (isShipped || order.sold === 1);
                                    const isProcessing = processingOrders.has(rowKey);
                                    const isCantFindBusy = cantFindBusy.has(rowKey);

                                    return (
                                        <tr key={`order-${index}-${order.id}-${rowKey || 'no-loc'}`} className="hover:bg-gray-50">
                                            <td className="py-4 pl-4 pr-3">
                                                <div className="h-10 w-10">
                                                    {order.photo ? (
                                                        <img src={order.photo} alt="" className="h-10 w-10 rounded-full object-cover"/>
                                                    ) : (
                                                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                                                <span className="text-xs font-medium text-gray-500">
                                                                    {order.product_name?.charAt(0) || 'N/A'}
                                                                </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-4 pl-4 pr-3 text-sm">
                                                <div className="font-medium text-gray-900 break-words">{order.product_name}</div>
                                                <div className="text-gray-500">{order.amazon_order_id}</div>
                                            </td>
                                            <td className="px-3 py-4 text-sm">
                                                <div className="text-gray-900">{order.asin}</div>
                                                <div className="text-gray-500">{order.fnsku}</div>
                                                <div className="text-gray-500">{order.sku}</div>
                                                <div className="text-gray-500">{order.display_condition}</div>
                                                <div className="text-gray-500">{order.gtin}</div>
                                                <div className="text-gray-500">{order.id}</div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                                {order.location || '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                    <span className={`inline-flex rounded-md px-2 py-1 text-xs font-medium
                                                        ${isAmazonShipped
                                                        ? 'bg-green-100 text-green-700'
                                                        : isPulledAwaitingSync
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {isAmazonShipped
                                                            ? 'Shipped'
                                                            : isPulledAwaitingSync
                                                            ? 'Pulled · awaiting sync'
                                                            : order.order_status}
                                                    </span>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {new Date(order.purchase_date).toLocaleDateString()}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                                {!(order.order_status === "Pending" || order.order_status === "Canceled") && (
                                                    <button
                                                        id={`ship-now-btn-${rowKey}`}
                                                        onClick={() => handleOpenModal(rowKey)}
                                                        disabled={order.order_status === "Shipped" || isShipped || isProcessing || order.sold === 1}
                                                        className={`px-3 py-2 text-sm font-semibold rounded-md ${
                                                            order.sold === 1 || order.order_status === "Shipped" || isShipped
                                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                : isProcessing
                                                                    ? 'bg-indigo-100 text-indigo-400 cursor-wait'
                                                                    : 'bg-indigo-600 text-white hover:bg-indigo-500'
                                                        }`}
                                                    >
                                                        {order.sold === 1 || order.order_status === "Shipped" || isShipped
                                                            ? 'Shipped'
                                                            : isProcessing
                                                                ? 'Processing...'
                                                                : 'Ship Now'
                                                        }
                                                    </button>
                                                )}
                                                {!order.calibrate_local_inventory_location_id && order.order_status !== "Canceled" && !isAmazonShipped && !isShipped && (
                                                    <button
                                                        onClick={() => openAssignModal(order)}
                                                        disabled={assignBusy.has(rowKey)}
                                                        title="Pick an available condition and assign one unit to this order."
                                                        className={`mt-2 block w-full px-3 py-2 text-xs font-medium rounded-md border ${
                                                            assignBusy.has(rowKey)
                                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait'
                                                                : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                                                        }`}
                                                    >
                                                        {assignBusy.has(rowKey) ? 'Assigning…' : 'Assign Inventory'}
                                                    </button>
                                                )}
                                                {!(order.order_status === "Shipped" || isShipped || order.order_status === "Canceled") && (
                                                    <button
                                                        onClick={() => openCantFindConfirm(order)}
                                                        disabled={isCantFindBusy}
                                                        title="Mark the assigned unit as unable to locate, and reassign to an in-stock unit if available."
                                                        className={`mt-2 block w-full px-3 py-2 text-xs font-medium rounded-md border ${
                                                            isCantFindBusy
                                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait'
                                                                : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
                                                        }`}
                                                    >
                                                        Unable to find unit. {order.other_available_count ?? 0} units in stock
                                                    </button>
                                                )}
                                                {/* Owner 2026-09-15: gated on Amazon's own status ONLY -- deliberately
                                                    NOT on isShipped. isShipped means the unit was PULLED on our end
                                                    (shippedRowsRef / sold === 1), and shipping is bought AFTER pulling,
                                                    so scanning used to hide the link exactly when it was needed. The
                                                    two buttons above keep using isShipped: assigning inventory and
                                                    reporting a unit unfindable genuinely are done before the pull. */}
                                                {!(order.order_status === "Shipped" || order.order_status === "Canceled") && order.amazon_order_id && (
                                                    <a
                                                        href={`https://sellercentral.amazon.com/orders-v3/order/${order.amazon_order_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open this order in Seller Central to buy shipping."
                                                        className="mt-2 block w-full px-3 py-2 text-xs font-medium rounded-md border bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 text-center"
                                                    >
                                                        Buy Shipping
                                                    </a>
                                                )}
                                            </td>
                                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm">
                                                <a href={`/orders/${order.id}`} className="text-indigo-600 hover:text-indigo-900">
                                                    View
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation modal for "Unable to find unit" */}
            {confirmOrder && (
                <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={cantFindBusy.has(confirmOrder.order_location_id) ? undefined : closeCantFindConfirm}></div>
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Mark unit as unable to locate?
                            </h3>

                            <div className="mt-4 bg-gray-50 p-4 rounded-md text-sm space-y-2">
                                <div className="text-gray-700">
                                    <span className="font-medium">Product:</span>{' '}
                                    {confirmOrder.product_name || confirmOrder.product_title || '(no name)'}
                                </div>
                                <div className="text-gray-700">
                                    <span className="font-medium">Order:</span>{' '}
                                    {confirmOrder.amazon_order_id}
                                </div>
                                <div className="text-gray-700">
                                    <span className="font-medium">Currently assigned location:</span>{' '}
                                    {confirmOrder.location || '-'} (inventory id {confirmOrder.calibrate_local_inventory_location_id || '0'})
                                </div>
                                <div className="text-gray-700">
                                    <span className="font-medium">Other units in stock:</span>{' '}
                                    {confirmOrder.other_available_count ?? 0}
                                </div>
                            </div>

                            <div className="mt-4 rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">
                                <p className="font-medium mb-1">This will:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Mark the currently-assigned unit as <code>unable_to_locate</code>.</li>
                                    {(confirmOrder.other_available_count ?? 0) > 0
                                        ? <li>Reassign the order to the next available unit with the same identifier.</li>
                                        : <li><strong>No replacement available</strong> — the order will be left unassigned for manual handling.</li>}
                                    <li>Update Amazon&apos;s MFN quantity for this SKU to the new available count.</li>
                                </ul>
                            </div>

                            {confirmResult && (
                                <div className={`mt-4 rounded-md p-4 text-sm ${confirmResult.isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-800'}`}>
                                    {confirmResult.message}
                                </div>
                            )}

                            <div className="mt-5 sm:mt-6 flex flex-row-reverse gap-3">
                                {!confirmResult && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={submitCantFind}
                                            disabled={cantFindBusy.has(confirmOrder.order_location_id)}
                                            className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-red-300 disabled:cursor-not-allowed"
                                        >
                                            {cantFindBusy.has(confirmOrder.order_location_id) ? 'Working…' : 'Confirm: Mark Unable to Locate'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={closeCantFindConfirm}
                                            disabled={cantFindBusy.has(confirmOrder.order_location_id)}
                                            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                )}
                                {confirmResult && (
                                    <button
                                        type="button"
                                        onClick={closeCantFindConfirm}
                                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                    >
                                        Close
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* "Assign Inventory" modal */}
            {assignModalOrder && (
                <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                         onClick={assignSubmitting ? undefined : closeAssignModal}></div>
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <h3 className="text-lg font-semibold text-gray-900">Assign Inventory</h3>
                            <div className="mt-4 bg-gray-50 p-4 rounded-md space-y-2 text-sm">
                                <div className="text-gray-700">
                                    <span className="font-medium">Product:</span>{' '}
                                    {assignModalOrder.product_name}
                                </div>
                                <div className="text-gray-700">
                                    <span className="font-medium">Order:</span>{' '}
                                    {assignModalOrder.amazon_order_id}
                                </div>
                                <div className="text-gray-700">
                                    <span className="font-medium">ASIN / Order condition:</span>{' '}
                                    {assignModalOrder.asin} — {assignModalOrder.display_condition}
                                </div>
                            </div>

                            <div className="mt-4">
                                {assignOptions === null && (
                                    <div className="text-sm text-gray-500">Loading inventory options…</div>
                                )}
                                {assignOptions !== null && assignOptions.length === 0 && (
                                    <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
                                        No available inventory for this ASIN under any condition.
                                    </div>
                                )}
                                {assignOptions !== null && assignOptions.length > 0 && (
                                    <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                                        {assignOptions.map(opt => {
                                            const checked = assignSelected === opt.identifier;
                                            return (
                                                <li key={opt.identifier} className="p-3">
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="assignOption"
                                                            value={opt.identifier}
                                                            checked={checked}
                                                            onChange={() => setAssignSelected(opt.identifier)}
                                                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                                        />
                                                        <span className="ml-3 flex-1 grid grid-cols-3 gap-2 items-center text-sm">
                                                            <span className="font-medium text-gray-900">
                                                                {opt.conditionName || opt.conditionTypeAfi}
                                                                {opt.isPreferred && (
                                                                    <span className="ml-1 text-xs text-green-700">(matches order)</span>
                                                                )}
                                                            </span>
                                                            <span className="text-gray-700">
                                                                {opt.qty} unit{opt.qty === 1 ? '' : 's'}
                                                            </span>
                                                            <span className="text-gray-500 text-xs">
                                                                {opt.identifier}{opt.sampleLocation ? ` @ ${opt.sampleLocation}` : ''}
                                                            </span>
                                                        </span>
                                                    </label>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            {assignError && (
                                <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800">
                                    {assignError}
                                </div>
                            )}

                            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                <button
                                    type="button"
                                    onClick={submitAssign}
                                    disabled={!assignSelected || assignSubmitting}
                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:bg-purple-300 disabled:cursor-not-allowed sm:col-start-2"
                                >
                                    {assignSubmitting ? 'Assigning…' : 'Assign this unit'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeAssignModal}
                                    disabled={assignSubmitting}
                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed sm:col-start-1 sm:mt-0"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}